import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Link, useBlocker, useNavigate, useParams } from "react-router-dom";
import { CoreButton } from "../../core/ui/button/button";
import { CoreSelect } from "../../core/ui/select/select";
import { CoreInput } from "../../core/ui/input/input";
import { CoreText, CoreTextType } from "../../core/ui/text/text";
import { ModalV2 } from "../../core/ui/modal/modal";
import { SessionControls } from "../authorization/session_controls";
import { ExamStore } from "./exam_store";
import { taskLabels } from "../task/task_editor_store";
import "../task/task.css";
import "./exam.css";

const SelectedTasks = observer(({ store }: { store: ExamStore }) => (
  <section className="panel">
    <CoreText
      as="h2"
      type={CoreTextType.header}
      text={`Состав и порядок (${store.model.taskIds.length}/500)`}
    />
    {!store.model.taskIds.length && (
      <p>
        Выберите задания из банка ниже. Пустую подборку тоже можно сохранить.
      </p>
    )}
    <ol className="exam-selected">
      {store.model.taskIds.map((id, index) => {
        const task = store.tasks[id];
        return (
          <li className="exam-row" key={id}>
            <div>
              <strong>
                {index + 1}. {task?.task?.title || "Задание отсутствует"}
              </strong>
              {task ? (
                <p className="muted">
                  {task.task
                    ? taskLabels[task.task.type]
                    : "Старое задание без содержимого"}{" "}
                  · Вес {task.scoreAmount} → {store.taskScores[index]} баллов ·{" "}
                  {task.status === "published" ? "Опубликовано" : "Черновик"}
                </p>
              ) : (
                <p role="alert">
                  Задание удалено из банка. Уберите его перед сохранением.
                </p>
              )}
            </div>
            <div className="row-actions">
              <CoreButton
                aria-label={`Поднять задание ${index + 1}`}
                disabled={index === 0}
                onClick={() => store.move(index, -1)}
              >
                ↑
              </CoreButton>
              <CoreButton
                aria-label={`Опустить задание ${index + 1}`}
                disabled={index === store.model.taskIds.length - 1}
                onClick={() => store.move(index, 1)}
              >
                ↓
              </CoreButton>
              <CoreButton
                aria-label={`Убрать задание ${index + 1}`}
                onClick={() => store.removeTask(id)}
              >
                Убрать
              </CoreButton>
            </div>
          </li>
        );
      })}
    </ol>
  </section>
));

const TaskPicker = observer(({ store }: { store: ExamStore }) => (
  <section className="panel">
    <CoreText as="h2" type={CoreTextType.header} text="Добавить из банка" />
    <div className="tag-input">
      <CoreInput
        native
        label="Поиск по названию задания"
        maxLength={200}
        value={store.query}
        onChange={store.setQuery}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            store.search();
          }
        }}
      />
      <CoreButton disabled={store.bankBusy} onClick={() => store.search()}>
        Найти
      </CoreButton>
    </div>
    {store.bankBusy && <p role="status">Загрузка банка…</p>}
    {store.bankError && <p role="alert">{store.bankError}</p>}
    {!store.bank.length && !store.bankBusy && <p>Заданий не найдено.</p>}
    {store.bank.map((task) => (
      <article className="exam-row" key={task._id}>
        <div>
          <strong>
            {task.task?.title || "Старое задание без содержимого"}
          </strong>
          <p className="muted">
            {task.task && taskLabels[task.task.type]} · {task.scoreAmount}{" "}
            баллов · {task.status === "published" ? "Опубликовано" : "Черновик"}
          </p>
          <p className="muted">
            {task.tags.join(", ")}
            {task.randomEligible === false
              ? " · Только для подборок администратора"
              : ""}
          </p>
        </div>
        <CoreButton
          disabled={
            !task.task ||
            !task._id ||
            store.model.taskIds.includes(task._id) ||
            store.model.taskIds.length >= 500
          }
          onClick={() => store.add(task)}
        >
          {task._id && store.model.taskIds.includes(task._id)
            ? "Добавлено"
            : "Добавить"}
        </CoreButton>
      </article>
    ))}
    {(store.bankPage > 0 || store.bank.length >= 10) && (
      <div className="row-actions">
        <CoreButton
          disabled={store.bankBusy || store.bankPage === 0}
          onClick={() => store.search(store.bankPage - 1, false)}
        >
          Предыдущие задания
        </CoreButton>
        <span>Страница {store.bankPage + 1}</span>
        <CoreButton
          disabled={store.bankBusy || store.bank.length < 10}
          onClick={() => store.search(store.bankPage + 1, false)}
        >
          Следующие задания
        </CoreButton>
      </div>
    )}
  </section>
));

export const ExamEditorScreen = observer(() => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [store] = useState(() => new ExamStore());
  const blocker = useBlocker(store.dirty || store.busy);
  useEffect(() => {
    store.load(id);
  }, [store, id]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (store.dirty || store.busy) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [store]);
  return (
    <main className="task-page">
      <SessionControls />
      <Link to="/exam">← Подборки заданий</Link>
      <header className="task-header">
        <CoreText
          as="h1"
          type={CoreTextType.big}
          text={id ? "Редактирование подборки" : "Новая подборка"}
        />
      </header>
      {store.busy && <p role="status">Загрузка / сохранение…</p>}
      {store.error && (
        <div role="alert" className="error-banner">
          {store.error}
          {!store.ready && (
            <CoreButton disabled={store.busy} onClick={() => store.load(id)}>
              Повторить
            </CoreButton>
          )}
        </div>
      )}
      {store.notice && (
        <p role="status" className="success-banner">
          {store.notice}
        </p>
      )}
      {store.ready && (
        <form
          onSubmit={async (event) => {
            event.preventDefault();
            const savedId = await store.save();
            if (savedId && !id)
              navigate(`/exam/${savedId}/edit`, { replace: true });
          }}
        >
          <fieldset className="editor-fields" disabled={store.busy}>
            <section className="panel">
              <CoreInput
                native
                label="Название подборки"
                value={store.model.title}
                maxLength={300}
                onChange={store.setTitle}
              />
              <div className="two-columns">
                <CoreSelect
                  label="Раздел портала"
                  value={store.model.kind || "test"}
                  options={[
                    { value: "test", label: "Текущие тесты" },
                    { value: "variant", label: "Варианты прошлых лет" },
                  ]}
                  onChange={(value) =>
                    store.setKind(value as "test" | "variant")
                  }
                />
                {store.model.kind === "variant" && (
                  <CoreInput
                    native
                    label="Год варианта"
                    inputMode="numeric"
                    value={
                      store.model.year == null ? "" : String(store.model.year)
                    }
                    onChange={store.setYear}
                  />
                )}
              </div>
              <CoreSelect
                label="Доступ в портале"
                value={store.model.status || "draft"}
                options={[
                  { value: "draft", label: "Черновик" },
                  { value: "published", label: "Опубликовано" },
                ]}
                onChange={(value) =>
                  store.setStatus(value as "draft" | "published")
                }
              />
              <div className="two-columns">
                <CoreInput
                  native
                  label="Общий балл теста"
                  inputMode="decimal"
                  placeholder="Сумма баллов заданий"
                  value={store.totalScoreInput}
                  onChange={store.setTotalScore}
                />
                <CoreSelect
                  label="Срок действия"
                  value={store.model.expiresAt != null ? "dated" : "unlimited"}
                  options={[
                    { value: "unlimited", label: "Бессрочный" },
                    { value: "dated", label: "До указанной даты" },
                  ]}
                  onChange={(value) =>
                    store.setExpiryEnabled(value === "dated")
                  }
                />
              </div>
              {store.model.expiresAt != null && (
                <CoreInput
                  native
                  label="Доступен до (Москва)"
                  inputType="datetime-local"
                  value={store.expiryInput}
                  onChange={store.setExpiresAt}
                />
              )}
              <p className="muted">
                Общий балл распределяется пропорционально весам заданий. Пустое
                поле сохраняет их исходные баллы.
              </p>
              <p className="muted">
                Для публикации добавьте хотя бы одно задание. Все задания должны
                быть опубликованы.
              </p>
            </section>
            <SelectedTasks store={store} />
            <TaskPicker store={store} />
            <footer className="panel editor-footer">
              <span>
                {store.dirty
                  ? "Есть несохранённые изменения"
                  : store.model._id
                    ? "Все изменения сохранены"
                    : "Подборка ещё не сохранена"}
              </span>
              <CoreButton type="submit" filled>
                Сохранить подборку
              </CoreButton>
            </footer>
          </fieldset>
        </form>
      )}
      {blocker.state === "blocked" && (
        <ModalV2
          isOpen
          titleId="leave-exam-title"
          onClose={() => blocker.reset()}
        >
          <CoreText
            as="h2"
            id="leave-exam-title"
            type={CoreTextType.header}
            text="Выйти из редактора?"
          />
          <p>
            {store.busy
              ? "Дождитесь окончания операции."
              : "Несохранённые изменения будут потеряны."}
          </p>
          <div className="row-actions">
            <CoreButton onClick={() => blocker.reset()}>Остаться</CoreButton>
            <CoreButton disabled={store.busy} onClick={() => blocker.proceed()}>
              Выйти без сохранения
            </CoreButton>
          </div>
        </ModalV2>
      )}
    </main>
  );
});
